import React, { useState } from 'react';
import axios from 'axios';
import { Mail, User as UserIcon, Phone, Lock, Eye, EyeOff, ArrowRight, Loader2, ShieldCheck } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Step 0 - Login / Signup with a password so the customer can log back in.
const EnrollAuth = ({ onAuthed }) => {
  const [stage, setStage] = useState('email'); // email | login | signup | setpass
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [knownName, setKnownName] = useState('');

  const validEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

  const handleContinueEmail = async (e) => {
    e.preventDefault();
    if (!validEmail(email.trim())) {
      toast({ title: 'Enter a valid email', variant: 'destructive' });
      return;
    }
    setBusy(true);
    try {
      const res = await axios.get(`${API}/auth/check-email/${encodeURIComponent(email.trim().toLowerCase())}`);
      const d = res.data || {};
      if (!d.exists) {
        setStage('signup');
      } else if (d.hasPassword) {
        setKnownName(d.name || '');
        setStage('login');
      } else {
        setKnownName(d.name || '');
        setName(d.name || '');
        setStage('setpass');
      }
    } catch (err) {
      setStage('signup');
    } finally {
      setBusy(false);
    }
  };

  const finish = (data) => {
    const { user, access_token } = data || {};
    if (!user) throw new Error('No user');
    try {
      localStorage.setItem('user', JSON.stringify(user));
      if (access_token) localStorage.setItem('token', access_token);
    } catch (_e) { /* ignore */ }
    toast({ title: `Welcome ${user.name?.split(' ')[0] || ''}!` });
    onAuthed(user);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!password) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email: email.trim().toLowerCase(), password });
      finish(res.data);
    } catch (err) {
      toast({ title: 'Login failed', description: err?.response?.data?.detail || 'Wrong password. Try again.', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    if (name.trim().length < 2) return toast({ title: 'Enter your full name', variant: 'destructive' });
    if (!/^[6-9]\d{9}$/.test(phone.trim())) return toast({ title: 'Enter a valid 10-digit mobile', variant: 'destructive' });
    if (password.length < 6) return toast({ title: 'Password must be 6+ characters', variant: 'destructive' });
    setBusy(true);
    try {
      const res = await axios.post(`${API}/auth/signup`, {
        name: name.trim(), email: email.trim().toLowerCase(), phone: phone.trim(), password,
      });
      finish(res.data);
    } catch (err) {
      toast({ title: 'Could not create account', description: err?.response?.data?.detail || 'Please try again.', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const handleSetPass = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast({ title: 'Password must be 6+ characters', variant: 'destructive' });
    setBusy(true);
    try {
      const res = await axios.post(`${API}/auth/set-password`, { email: email.trim().toLowerCase(), password });
      finish(res.data);
    } catch (err) {
      const detail = err?.response?.data?.detail || '';
      if (detail.includes('log in')) { setStage('login'); toast({ title: 'You already have a password', description: 'Please log in.' }); }
      else toast({ title: 'Could not set password', description: detail || 'Please try again.', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-md mx-auto">
      <div className="text-center mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">
          {stage === 'login' ? 'Welcome back' : stage === 'setpass' ? 'Set your password' : 'Create your account'}
        </h1>
        <p className="text-slate-500 text-sm mt-2">
          {stage === 'email' && 'Enter your email to start your store.'}
          {stage === 'login' && `Log in as ${knownName || email}.`}
          {stage === 'signup' && 'Sign up with a password so you can log in anytime.'}
          {stage === 'setpass' && 'We found your account — set a password to continue.'}
        </p>
      </div>

      <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-5 sm:p-7">
        {stage === 'email' && (
          <form onSubmit={handleContinueEmail} className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500 mb-1.5 inline-block">Gmail / Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input data-testid="auth-email" type="email" autoComplete="email" value={email}
                  onChange={(e) => setEmail(e.target.value)} placeholder="you@gmail.com" className="pl-10 h-12" />
              </div>
            </div>
            <Button type="submit" size="lg" disabled={busy} data-testid="auth-continue"
              className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Continue <ArrowRight className="w-4 h-4 ml-2" /></>}
            </Button>
          </form>
        )}

        {stage === 'login' && (
          <form onSubmit={handleLogin} className="space-y-4">
            <PasswordField password={password} setPassword={setPassword} showPwd={showPwd} setShowPwd={setShowPwd} label="Password" />
            <Button type="submit" size="lg" disabled={busy} data-testid="auth-login"
              className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Log In'}
            </Button>
            <button type="button" onClick={() => setStage('email')} className="text-xs text-slate-500 hover:text-slate-700 w-full text-center">Use a different email</button>
          </form>
        )}

        {stage === 'setpass' && (
          <form onSubmit={handleSetPass} className="space-y-4">
            <PasswordField password={password} setPassword={setPassword} showPwd={showPwd} setShowPwd={setShowPwd} label="Create a password" />
            <Button type="submit" size="lg" disabled={busy} data-testid="auth-setpass"
              className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save & Continue'}
            </Button>
          </form>
        )}

        {stage === 'signup' && (
          <form onSubmit={handleSignup} className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500 mb-1.5 inline-block">Full name</Label>
              <div className="relative">
                <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input data-testid="auth-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Aditya Verma" className="pl-10 h-12" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500 mb-1.5 inline-block">Mobile (WhatsApp)</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <span className="absolute left-9 top-1/2 -translate-y-1/2 text-sm text-slate-500">+91</span>
                <Input data-testid="auth-phone" type="tel" maxLength={10} value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="9876543210" className="pl-16 h-12" />
              </div>
            </div>
            <PasswordField password={password} setPassword={setPassword} showPwd={showPwd} setShowPwd={setShowPwd} label="Create a password" />
            <Button type="submit" size="lg" disabled={busy} data-testid="auth-signup"
              className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Account'}
            </Button>
            <button type="button" onClick={() => setStage('email')} className="text-xs text-slate-500 hover:text-slate-700 w-full text-center">Use a different email</button>
          </form>
        )}

        <p className="text-[11px] text-slate-400 text-center mt-4 flex items-center justify-center gap-1">
          <ShieldCheck className="w-3 h-3 text-green-500" /> Your details are safe and never shared.
        </p>
      </div>
    </div>
  );
};

const PasswordField = ({ password, setPassword, showPwd, setShowPwd, label }) => (
  <div>
    <Label className="text-xs uppercase tracking-wide text-slate-500 mb-1.5 inline-block">{label}</Label>
    <div className="relative">
      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
      <Input data-testid="auth-password" type={showPwd ? 'text' : 'password'} value={password}
        onChange={(e) => setPassword(e.target.value)} placeholder="\u2022\u2022\u2022\u2022\u2022\u2022" className="pl-10 pr-10 h-12" />
      <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
        {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  </div>
);

export default EnrollAuth;
